import * as React from 'react';

import { m } from 'framer-motion';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { decryptData, encryptData } from 'src/utils/Crypto';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AppVersion from 'src/components/AppVersion';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { isTenantDomain, getTenantSlug } from 'src/utils/domainContext';

// components
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import axiosInstance from 'src/api/axiosInstance';
import { RHFAutocomplete } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { useForm } from 'react-hook-form';
import { fetchUserRoles, logoutUser } from 'src/redux/thunks/authThunks';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import UpgradeIcon from '@mui/icons-material/Upgrade';

export default function AccountPopover() {
  // Can be null when user login or there is no profile photo
  const profilePhoto = useSelector((state) => state.user.profilePhoto);
  const organizationName = useSelector((state) => state.plan.orgFullName);
  const planName = useSelector((state) => state.plan.planName);

  const router = useRouter();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const token = sessionStorage.getItem('token');
  const popover = usePopover();
  const [currentPlan, setCurrentPlan] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));
  const isSuperAdmin = subscr?.roleName?.trim().toLowerCase() === 'super admin';
  const decryptedEmail = user?.email ? decryptData(user.email) : '';
  const [selectedSite, setSelectedSite] = useState('');
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // State to hold profile picture URL
  const [profilePic, setProfilePic] = useState([]);

  const handleLogout = async () => {
    try {
      const resultAction = await dispatch(logoutUser());

      // ✅ Show success snackbar only if fulfilled
      if (logoutUser.fulfilled.match(resultAction)) {
        enqueueSnackbar('Logged out successfully', { variant: 'success' });
      }

      // ❌ Never show error snackbar on logout
      popover.onClose();

      // ✅ Always redirect
      router.replace(paths.auth.jwt.login);
    } catch (error) {
      console.error('Logout error:', error);

      // Even on unexpected errors, still logout client-side
      popover.onClose();
      router.replace(paths.auth.jwt.login);
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    router.push(path);
  };

  const fetchCurrentPlan = async () => {
    try {

      const response = await axiosInstance.get(
        '/rest/dynamic-billing/current-plan',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCurrentPlan(response.data);


      return response.data;

    } catch (error) {

      console.error(
        'CURRENT_PLAN_ERROR',
        error
      );

      return null;
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  const fetchData = async () => {
    try {
      const encryptedId = await fetchEncryptedToken(user.userId, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');
      const response = await axiosInstance.get(`/rest/users/getProfilePhoto/${encryptedId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setProfilePic(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sites, setSites] = useState([]);

  const defaultValues = useMemo(
    () => ({
      currentSites: sites || null,
    }),
    [sites]
  );

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const fetchSites = useCallback(async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData) {
        console.error('User data not found in sessionStorage');
        return;
      }

      // Prepare payload after confirming userData exists
      const payload = { userId: encryptData(String(userData.userId)) };

      const availableRoles =
        JSON.parse(localStorage.getItem('availableRoles')) || [];

      setSites(availableRoles);

      const activeRoles = availableRoles.filter((role) => {
        if (!role.endDate) {
          return true;
        }

        return new Date(role.endDate) > new Date();
      });

      // Extract unique sites based on subscrSiteName
      const uniqueSites = activeRoles.reduce((accumulator, currentItem) => {
        const exists = accumulator.some(
          (site) => site?.subscrSiteName === currentItem?.subscrSiteName
        );
        if (!exists) accumulator.push(currentItem);
        return accumulator;
      }, []);

      // Sort alphabetically (case-insensitive)
      const sortedSites = uniqueSites.sort((siteA, siteB) => {
        const nameA = siteA.subscrSiteName.toLowerCase();
        const nameB = siteB.subscrSiteName.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setSites(sortedSites);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]);

  useEffect(
    () => {
      fetchSites();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Set the first site as default only when sites array changes
    if (sites.length > 0 && !selectedSite) {
      const defaultSite =
        JSON.parse(localStorage.getItem('selectedSubscrSite')) ||
        sites[0];
      setSelectedSite(defaultSite); // Set the first site as default if no site is selected
      // Dispatch here immediately after setting
      dispatch(
        fetchUserRoles({
          userId: user.userId,
          siteId: decryptData(defaultSite.subscrSiteId),
          token: sessionStorage.getItem('token'),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedSite, sites]);

  useEffect(() => {
    setProfilePic(profilePhoto);
  }, [profilePhoto]);

  // Function to handle site selection
  const handleSiteSelection = (site) => {
    setSelectedSite(site); // Update selectedSite when a different site is selected
    localStorage.setItem('selectedSubscrSite', JSON.stringify(site));
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          transition: 'all 0.2s ease',
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          }),
        }}
      >
        <Avatar
          src={profilePic || JSON.parse(localStorage.getItem('user'))?.userName}
          alt={JSON.parse(localStorage.getItem('user'))?.userName}
          sx={{
            width: 38,
            height: 38,
            fontWeight: 600,
            border: (theme) => `2px solid ${theme.palette.background.paper}`,
          }}
        />
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{
          width: 320,
          p: 0,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
        }}
      >
        {/* 👤 User Info */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(
                theme.palette.primary.main,
                0.08
              )})`,
          }}
        >
          <Avatar
            src={profilePic || JSON.parse(localStorage.getItem('user'))?.userName}
            alt={JSON.parse(localStorage.getItem('user'))?.userName}
            sx={{
              width: 42,
              height: 42,
              fontWeight: 600,
              border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            {/* Username */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {JSON.parse(localStorage.getItem('user'))?.userName}
            </Typography>

            {/* Email */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: 'text.disabled',
                fontSize: '0.7rem',
              }}
              noWrap
            >
              {decryptedEmail}
            </Typography>

            {/* Organization Name */}
            {organizationName && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  mt: 0.4,
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Iconify
                  icon="solar:buildings-2-bold-duotone"
                  width={12}
                  sx={{ color: 'primary.main', flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    lineHeight: 1,
                  }}
                  noWrap
                >
                  {organizationName}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Divider />

        {/* 🌐 Site Selector */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <FormProvider methods={methods}>
            <RHFAutocomplete
              name="currentSites"
              label="Current Site"
              placeholder="Select Site"
              options={sites}
              getOptionLabel={(option) => option?.subscrSiteName}
              value={selectedSite}
              onChange={(event, newValue) => handleSiteSelection(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option?.subscrSiteName}>
                  {option?.subscrSiteName || '-'}
                </li>
              )}
            />
          </FormProvider>
        </Box>

        <Divider />

        {/* ⚙️ Profile */}
        <MenuItem
          onClick={() => handleClickItem(paths.dashboard.user.account)}
          sx={{
            mx: 1,
            borderRadius: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Iconify icon="solar:settings-bold" width={18} />
          Profile Settings
        </MenuItem>

        {isSuperAdmin && (
          <MenuItem
            onClick={() => {
              popover.onClose();
              window.open('/pricing', '_blank', 'noopener,noreferrer');
            }}
            sx={{
              mx: 1,
              borderRadius: 1.5,
              py: 1,
              fontWeight: 600,
              color: '#6a1b9a',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-crown-icon lucide-crown">
              <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" />
              <path d="M5 21h14" />
            </svg>
            Upgrade Plan

            {/* Active plan indicator: green dot + plan name */}
            {currentPlan && (
              <Box
                sx={{
                  ml: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 1,
                  py: 0.3,
                  borderRadius: 5,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                  border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                {/* Green active dot */}
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'success.dark',
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  {(currentPlan?.subscriptionStatus?.toUpperCase() === 'TRIAL' || currentPlan?.paymentMode?.toUpperCase() === 'TRIAL')
                    ? 'TRIAL'
                    : (currentPlan?.planName || 'FREE TRIAL')}
                </Typography>
              </Box>
            )}
          </MenuItem>
        )}

        {/* 🚪 Logout */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            m: 1,
            borderRadius: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontWeight: 600,
            color: 'error.main',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha('#ff0000', 0.08),
            },
          }}
        >
          <Iconify icon="solar:logout-2-bold" width={22} sx={{ transform: 'rotate(180deg)' }} />
          Logout
        </MenuItem>
        {/* App Version */}
        <Box
          sx={{
            px: 2,
            pb: 1.2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
              border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <AppVersion size="small" variant="inline" showLabel />
          </Box>
        </Box>




      </CustomPopover>
    </>
  );
}
